import {
  Column,
  EnquiryHandle,
  IServerSideGetRowsParams,
  ServerSideCriteria,
  useServerEnquiry,
} from '@access-erp/common';
import { Guid } from 'guid-typescript';
import { useEffect, useRef, useState } from 'react';

import { useColumns } from './hooks/useColumns';
import { useRightPanel } from './hooks/useRightPanel';
import { GetPaginatedFilteredPurchaseOrdersVariables, }
  from '@/graphql/contracts/_generated_/getPaginatedFilteredPurchaseOrders.query';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import { CurrencyType, Forms } from '@/graphql/contracts/_generated_/contracts';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { getEnabledCurrencies } from '@/utils/data-helpers/currecies';
import AddPurchaseOrderModal from
  // eslint-disable-next-line max-len
  '../ContractDetails/RightPanel/Billing/AssignPurchaseOrderForLines/components/AddPurchaseOrderModal/AddPurchaseOrderModal';
import { PurchaseOrderWithId } from '@/utils/data-helpers/types';
import { EditPurchaseOrderModal } from './components/EditPurchaseOrderModal/EditPurchaseOrderModal';
import { DeletePurchaseOrdersModal } from './components/DeletePurchaseOrdersModal/DeletePurchaseOrdersModal';
import { replaceAdvancedValueType } from '@/components/common/EnquiriesScreen/utils/advancedFilterHelpers';
import useGetPurchaseOrders from '../ContractDetails/ContractLines/hooks/useGetPurchaseOrders';

const PurchaseOrdersComponent = () => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const gridRef = useRef<EnquiryHandle<PurchaseOrderWithId>>(null);
  const { headerColumns, goToPurchaseOrdersBySearch } = useColumns();
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();
  const { getUserRole } = useGetUserRole();
  const [currencies, setCurrencies] = useState<CurrencyType[]>([]);
  const { getPurchaseOrders } = useGetPurchaseOrders();
  const enquiry = useServerEnquiry<
  PurchaseOrderWithId,
  GetPaginatedFilteredPurchaseOrdersVariables['filter']
  >({
    gridRef,
    headerColumns: headerColumns as Column<PurchaseOrderWithId>[],
    initialFilter: replaceAdvancedValueType(savedFilters),
  });

  useEffect(() => {
    const getInitialData = async () => {
      const enabeledCurrencies = getEnabledCurrencies();
      setCurrencies(enabeledCurrencies);
    };

    getInitialData();
  }, [getUserRole]);
  const {
    tabsConfiguration,
    isVisibleAddModal,
    setIsVisibleAddModal,
    isVisibleEditModal,
    setIsVisibleEditModal,
    isVisibleDeleteModal,
    setIsVisibleDeleteModal,
  } = useRightPanel({
    gridRef: gridRef.current ?? undefined,
    selectedHeaderRows: enquiry.selectedHeaderRows,
  });

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  const getData = async (
    params: IServerSideGetRowsParams,
    serverSideCriteria: ServerSideCriteria,
  ) => {
    await getSavedFilterIfAny(Forms.PurchaseOrders, params);

    const purchaseOrdersData = await getPurchaseOrders({
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter: {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        ...serverSideCriteria.groupingFilters,
      },
      sort: serverSideCriteria.userSetSorting,
      group: serverSideCriteria.groupByColumn as any,
    });

    setPageIsLoading(false);

    const searchIsCleared = enquiry.serverSide?.current?.search === '';
    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToPurchaseOrdersBySearch(enquiry.serverSide?.current?.search);
    }

    return {
      items:
        purchaseOrdersData?.items?.map((el) => ({
          ...el,
          id: el?.id && !Guid.parse(el.id).isEmpty() ? el.id : Guid.create().toString(),
        })) ?? [],
      pageInfo: {
        hasNextPage: purchaseOrdersData?.pageInfo.hasNextPage,
        hasPreviousPage: purchaseOrdersData?.pageInfo.hasPreviousPage,
        endCursor: purchaseOrdersData?.pageInfo?.endCursor,
        startCursor: purchaseOrdersData?.pageInfo?.startCursor,
      },
    };
  };

  const refreshGrid = () => {
    gridRef.current?.deselectRows();
    gridRef.current?.refreshServerSideGrid({
      purge: true,
    });
  };

  return (
    <>
      <ContractsEnquiriesScreen
        ref={gridRef}
        enquiry={enquiry}
        filterForm={Forms.PurchaseOrders}
        formName={'PurchaseOrders'}
        headerProps={{
          primaryTitle: 'Purchase Orders',
          secondaryTitle: 'List',
          searchPlaceholder: 'Start typing PO Number or Account Name',
          search: searchTextFromQuery,
          showExportAll: false,
          showExportCurrent: false,
        }}
        detailProps={{
          exportFileName: 'Purchase Orders',
          rowSelection: { mode: 'singleRow' },
          rowGroupPanelShow: 'always',
          masterDetail: true,
          getData: getData as any,
          pageSize: CONTRACTS_PER_PAGE,
          suppressRowClickSelection: false,
          loading: pageIsLoading,
        }}
        rightPanelTabs={tabsConfiguration}
      />

      <AddPurchaseOrderModal
        refreshGrid={refreshGrid}
        onClose={() => setIsVisibleAddModal(false)}
        isOpen={isVisibleAddModal}
        currencies={currencies}
        enableCustomerSelection={true}
      />

      {enquiry.selectedHeaderRows?.[0]?.data && isVisibleDeleteModal && (
        <DeletePurchaseOrdersModal
          refreshGrid={refreshGrid}
          onClose={() => setIsVisibleDeleteModal(false)}
          isVisible={isVisibleDeleteModal}
          purchaseOrder={enquiry.selectedHeaderRows[0]?.data}
        />
      )}

      {enquiry.selectedHeaderRows?.[0]?.data && isVisibleEditModal && (
        <EditPurchaseOrderModal
          refreshGrid={refreshGrid}
          onClose={() => setIsVisibleEditModal(false)}
          isVisible={isVisibleEditModal}
          purchaseOrder={enquiry.selectedHeaderRows[0]?.data}
          currencies={currencies}
        />
      )}
    </>
  );
};

export default PurchaseOrdersComponent;
